import {ApplicationConfiguration} from "./interfaces";
import {extend} from "underscore";
const Environments = require("../../../../webpack-config/environments");

// This should reflect what is set in the webpack config by DefinePlugin.
const {
    APP_CONFIG,
    NODE_ENV,
    PORT,
    TARGET_ENV
} = process.env;

const providedConfig = typeof APP_CONFIG === "string" ? JSON.parse(APP_CONFIG) : APP_CONFIG;

export {ApplicationConfiguration};

// FIXME FIND BETTER SOLUTION
const findQueryParameter = (url: string, name: string | string[]) => {
    // window.location.search can very well be empty...
    if (url) {
        const names = Array.isArray(name) ? name : [name];
        const parameters = url.slice(url.indexOf("?") + 1).split("&");

        let pair;
        let i;
        let j;

        for (j = 0; j < names.length; j++) {
            name = names[j];
            for (i = 0; i < parameters.length; i++) {
                pair = parameters[i].split("=");
                if (pair[0] === name) {
                    return pair[1]; // can be empty!
                }
            }
        }
    }

    return undefined;
};

/**
 * Default converters from actual type to desired type, e.g. "number('42', 'string') -> 42.
 */
const converters = {
    string(value: string, type: string) {
        return type === "undefined" ? "" : value.toString();
    },
    number(value: string, type: string) {
        if (type === "boolean" || type === "undefined") {
            return value ? 1 : 0;
        }
        return parseInt(value.toString(), 10) || undefined; // NaN -> undefined
    },
    boolean(value: string, type: string) {
        if (type === "number") {
            return !!value;
        } else if (type === "undefined") {
            return false;
        }
        return /yes|true|on/i.test(value.toString());
    },
    function(value: string) {
        return function() { return value; };
    }
};

/**
 * A configuration, where each raw configuration value can be access directly as a property, or via the
 * "val(options)" method that supports query parameter overwriting and/or type coercion.
 *
 * @author Gunni Rode
 * @author Bruce Mackenzie Nielsen
 *
 * @class Configuration
 */
export class Configuration {

    /**
     * Returns the specified configuration value. <p>
     *
     * Options:
     * <pre>
     * {
     *    name:         String|String[],
     *    configName:   String,
     *    type:         String,
     *    url:          String|Boolean,
     *    defaultValue: *,
     *    converter:    Function -> fn(value:<args.type>, type:String): <options.type>
     * }
     * </pre>
     *
     * If "options.url" is not false and if a query parameter named "options.name" is specified, it will be used.
     * The query parameter is that of <tt>window.location</tt> unless "options.url" is specified as a string. If no
     * such query parameter, the configuration values "this.name", secondarily "this.configName", are tried. If the
     * value is still undefined, "options.defaultValue" is used, if any. <p>
     *
     * If "options.type" is specified, any value above must match said type or it will not be used, eventually
     * returning undefined if neither match. If no type coercion, the first defined value is used. The
     * default types "string", "number", and "boolean" are supported out of the box. "function" will wrap
     * any non-function type in a function that will return said value. "object" has no conversion. <p>
     *
     * If "options.converter" is a conversion type like "fn(value, jsType):options.type", it will be used for the
     * type coercion if the types does not already match. No conversion will ever be made if the types match. Any
     * valid JS type, e.g. also "object" and "function", can be used if "options.converter" can handle it. <p>
     *
     * Examples:
     * <pre>
     *   // Looks up query parameter "foo", then "this.foo" if no such value, and return the value what ever type
     *   // it is, e.g. a string if found as a query parameter, but can be any value if found in this
     *   // configuration directly...
     *   var a = Configuration.val({name: "foo"});
     *
     *   // Different query parameter name: looks up query parameter "foo", then "this.bar" if no such value, and
     *   // return the value what ever type it is, e.g. a string if found as a query parameter, but can be any
     *   // value if found in this configuration directly...
     *   var b = Configuration.val({name: "foo", configName: "bar"});
     *
     *   // Coerce type: looks up query parameter "foo", then "this.foo" if no such value, and coerce value
     *   // into a number, or return undefined if it cannot be coerced into a number...
     *   var c = Configuration.val({name: "foo", type: "number"});
     *   // -> typeof c is "number" or "undefined"
     *
     *   // Coerce type: looks up query parameter "foo", then "this.foo" if no such value, and coerce value
     *   // into a number, or return default value if it cannot be coerced into a number. Notice the default
     *   // value will also be coerced; if not possible, undefined will still be returned...
     *   var c = Configuration.val({name: "foo", type: "number", defaultValue: "42"});
     *   // -> typeof c is "number" or "undefined". If no foo found, 42 is returned
     *
     *   // Lookup based on defaults: looks up query parameter "foo", then "this.foo" if no such value, and
     *   // coerce value into a number, or return default value (42) if it cannot be coerced into a number.
     *   var c = Configuration.val({defaultValue: 42}, {name: "foo", type: "number"});
     *   // -> typeof c is "number" or "undefined"
     *   ..
     * </pre>
     *
     * @param  {*} options          The options to use; can be null/undefined if <tt>defaultOptions</tt> are
     *                              supplied. See examples above.
     * @param  {*} [defaultOptions] Optional default options to add to <tt>options</tt> where not supplied;
     *                              can be falsey. Useful if <tt>options</tt> is supplied by a given context,
     *                              e.g. a default value, and these default options will fill in the blanks
     *                              needed, e.g. the actual config name to look up. See example above.
     *
     * @return {*} The corresponding configuration value, or undefined if not found or not matching "options.type".
     */
    static val(options: {}, defaultOptions?: {}) {
        if (defaultOptions) {
            options = extend(options || {}, defaultOptions);
        }
        const windowDefined = "undefined" !== typeof window;
        // @ts-ignore
        var optionValue = options.url || windowDefined ? window.location.search : "";
        // Type of value always a "string" or "undefined" here; can never be null (object)...
        // @ts-ignore
        let value = options.url !== false ?
         // @ts-ignore
        findQueryParameter(optionValue, options.name) :
        undefined;
        let type = typeof value;

        if (type === "undefined") {
            // Type of value can be any type here, including "undefined"...
             // @ts-ignore
            value = this[options.configName || options.name];
            // Make sure null means undefined...
            if (value === null) {
                value = undefined;
            }
            type = typeof value;
        }
        // If a type has been specified and it matches, simply return it...
        // @ts-ignore
        if (type === options.type) {
            return value;
        // No specified type, so return value or what ever default value if the value is undefined...
         // @ts-ignore
        } else if (!options.type) {
             // @ts-ignore
            return type === "undefined" ? options.defaultValue : value;
        }

        // See if we have a converter from "type" to "options.type"...
         // @ts-ignore
        const converter = options.converter || converters[options.type];
         // @ts-ignore
        const defaultType = typeof options.defaultValue;

        if (converter) {
            // Converter will return valid type, or undefined...
            if (type !== "undefined") {
                value = converter(value, type);
            }
            // Still no value, so try default...
            if (typeof value === "undefined") {
                // Proper type or do we need to convert default value as well?...
                // @ts-ignore
                return defaultType === options.type ?
                 // @ts-ignore
                options.defaultValue : converter(options.defaultValue, defaultType);
            }
            return value;
        }
        // No converter, but wrong type, which is not what we want. Check if we have a matching default value,
        // or we must return undefined...
         // @ts-ignore
        return defaultType === options.type ? options.defaultValue : undefined;
    }
}

// Merge global config into our configuration to avoid awkward Configuration.config syntax. This will *not*
// overwrite our special "val" function!...

export default {
    ...providedConfig,
    production: NODE_ENV === "production",
    port: PORT || 8081,
    targetEnv: TARGET_ENV || Environments.Names.DEV,
} as ApplicationConfiguration;



// WEBPACK FOOTER //
// ./src/shared/xp/config/index.ts